import { Box } from '@cdl/heartbeat-react';
import { line } from 'd3-shape';
import React, { useMemo } from 'react';
import { Amplitudes } from '../../ecg/types/signal';
import { roundPixel } from '../../utils/math';
import { useCoordinatesScale } from '../hooks/useCoordinatesScale';
import { useLeadScale } from '../hooks/useLeadScale';
import { useRenderedChunk } from '../hooks/useRenderedChunk';
import { useStrip } from '../hooks/useStrip';
import { GRID_UNIT_SIZE } from '../utils/graph.utils';

interface Props {
  color?: string;
  frequency: number;
  lead: Amplitudes;
}

export const Lead: React.FC<Props> = ({ color, lead, frequency }) => {
  const { xScale, yScale, onset } = useStrip();
  const leadScale = useLeadScale(xScale);
  const coordinatesScale = useCoordinatesScale(leadScale, frequency);
  const timeToIndex = (n: number) =>
    Math.floor(coordinatesScale.invert(xScale(n)));
  const { start, end } = useRenderedChunk();
  const xTranslation = useMemo(
    () => xScale(start + onset),
    [start, onset, xScale],
  );
  const startIndex = timeToIndex(start);
  const endIndex = timeToIndex(end);
  const leadData = useMemo(
    () => lead.slice(startIndex, startIndex + (endIndex - startIndex)),
    [lead, startIndex, endIndex],
  );

  // we need to invert the yScale because the SVG coordinate system is inverted
  // compare to mathematical coordinates
  // 0,0 in SVG is the top left corner, while in math it is the bottom left
  const pathData = useMemo(() => {
    const lineFn = line<number>()
      .x((_, index) => roundPixel(coordinatesScale(index)))
      .y((n) => roundPixel(-yScale(n)));

    return lineFn(leadData) || undefined;
  }, [coordinatesScale, yScale, leadData]);

  return (
    <g transform={`translate(${xTranslation})`}>
      <Box
        as='path'
        data-testid='lead-path'
        className='lead'
        pointerEvents='none'
        strokeWidth={GRID_UNIT_SIZE}
        stroke={color ?? 'neutral.900'}
        fill='none'
        d={pathData}
      />
    </g>
  );
};
